import cloneDeep from 'lodash.clonedeep';
import { getActivitiesWithStudentInfo } from '../../../../services/API';
import { getScreeningWindowsForSchoolYear } from '../../../../services/screeningWindowService';
import PromisePool from '@supercharge/promise-pool'

import { generateBenchmarkReport } from './generateBenchmarkReport';
import { states } from '../../values/enums';
import SRS from '../../../../services/StudentRecordService';
import { getPR } from '../../functions/getPR';
import { adjustPercentile } from '../adjustPercentile';

export async function fetchStudentTrends(students, language) {
  const batchSize = 5; // Adjust this number as needed
  const studentTrendStudentInput = students.map(s => ({ studentId: s.id, grade: s.grade }));

  // split the students into batches and fetch trends for each batch
  const batchedStudentTrend = [];
  for (let i = 0; i < studentTrendStudentInput.length; i += batchSize) {
    batchedStudentTrend.push(studentTrendStudentInput.slice(i, i + batchSize));
  }
  const resultTrends = [];
  const date = new Date();
  await PromisePool
    .withConcurrency(3)
    .for(batchedStudentTrend)
    .process(async (batch) => {
      const trends = await SRS.getStudentTrend(batch, date, language);
      if (!trends) return;
      resultTrends.push(...trends.map(t => ({ ...t, trend: t.trend ? Number(t.trend.toFixed(2)) : t.trend })));
    });
  return resultTrends;
}

export async function fetchActivitiesForClassProgressReport(params) {
  const { students, selectedLocale } = params;

  const windows = getScreeningWindowsForSchoolYear();
  const activitiesPerWindow = [];

  const limit = 30;
  const type = ['assessment', 'dyslexia', 'letterNamingAndSounds'];
  const equating = true;
  const studentBatchSize = 5; // Number of students to process in each batch

  // Iterate through each window of the school year
  for (const window of windows) {
    const { startDate, endDate } = window;
    const statuses = ["not_started", "scored"];
    const displayStatuses = ["completed", "rescored", "prereader"];

    // Process students in batches
    const studentBatches = [];
    for (let i = 0; i < students.length; i += studentBatchSize) {
      studentBatches.push(students.slice(i, i + studentBatchSize));
    }

    // Collect activities for the current window
    const windowActivities = [];

    // Use PromisePool to limit concurrent calls for each batch
    await PromisePool
      .withConcurrency(3) // Set the maximum number of concurrent batches
      .for(studentBatches)
      .process(async (studentBatch) => {
        const studentActivities = await getActivitiesWithStudentInfo(
          studentBatch, limit, type, statuses, equating, selectedLocale, startDate, endDate, displayStatuses
        );
        windowActivities.push(...studentActivities); // Collect activities for the current window
      });

    // Push the activities for the current window to the main array
    activitiesPerWindow.push(windowActivities);
  }

  // Return an array of windows, where each element is an array of activities for that window
  return activitiesPerWindow;
}

export async function fetchMasteriesForClassProgressReport(params) {
  const { students, selectedLocale, selectedStatePercentileCuts } = params;
  const studentIds = students.map(student => student.id);
  const useARM = selectedStatePercentileCuts && selectedStatePercentileCuts.state !== "US";
  const {batchReadingRopeReport} = await SRS.getReadingRopeData({ studentIds, locale: selectedLocale, useARM });
  const masteriesByStudentId = {};
  for (const readingRopeReport of batchReadingRopeReport) {
    const {studentId, threads} = readingRopeReport;
    const wordRecognitionThread = threads.find(thread => thread.label === 'Word Recognition');
    const comprehensionThread = threads.find(thread => thread.label === 'Comprehension');
    const phonologicalAwarenessDomain = wordRecognitionThread.domains.find(domain => domain.label === 'Phonological Awareness');
    const decodingDomain = wordRecognitionThread.domains.find(domain => domain.label === 'Decoding');
    const vocabularyDomain = comprehensionThread.domains.find(domain => domain.label === 'Vocabulary');
    const masteryPRs = {
      phonologicalAwarenessPR: adjustPercentile(phonologicalAwarenessDomain.mastery.value),
      decodingPR: adjustPercentile(decodingDomain.mastery.value),
      wordRecognitionPR: adjustPercentile(wordRecognitionThread.mastery.value),
      vocabularyPR: adjustPercentile(vocabularyDomain.mastery.value),
      comprehensionPR: adjustPercentile(comprehensionThread.mastery.value),
    }
    masteriesByStudentId[studentId] = masteryPRs;
  }
  return masteriesByStudentId;
}

export async function generateClassProgressReport(params) {
  const {
    students,
    metricId,
    scaleId,
    selectedBenchmark,
    customBenchmarks,
    selectedLocale,
    isBenchmarkOnly,
    activities,
    masteries,
    studentTrends
  } = params;
  const windows = getScreeningWindowsForSchoolYear();
  const reportDataByStudentId = {};

  const studentOrder = [];
  for (const [index, window] of windows.entries()) {
    const studentActivities = cloneDeep(activities[selectedLocale][index]);

    const {scores: benchmarkScores} = await generateBenchmarkReport(studentActivities, metricId, scaleId, selectedBenchmark, customBenchmarks, selectedLocale, students, true, window);
    for (const benchmarkScore of benchmarkScores) {
      if (isInvalidScore(benchmarkScore)) continue;
      const {ui, displayScore: score, PR} = benchmarkScore;
      const {label, value: studentId} = ui;
      if (!(studentId in reportDataByStudentId)) {
        reportDataByStudentId[studentId] = {name: label};
        studentOrder.push(studentId);
      }
      const formattedScore = score ? Math.round(score * 100)/100 : score;
      reportDataByStudentId[studentId][window.id] = { score: formattedScore, PR, scaleId };
    }

    const {scores: latestScores} = await generateBenchmarkReport(studentActivities, metricId, scaleId, selectedBenchmark, customBenchmarks, selectedLocale, students, false, window);
    for (const latestScore of latestScores) {
      if (isInvalidScore(latestScore)) continue;
      const {ui, displayScore: score} = latestScore;
      const {value: studentId} = ui;

      if (!(studentId in reportDataByStudentId)) {
        reportDataByStudentId[studentId] = {name: ui.label};
        reportDataByStudentId[studentId][window.id] = {};
        studentOrder.push(studentId);
      }

      const formattedLatestScore = score ? Math.round(score * 100)/100 : score;
      if (!reportDataByStudentId[studentId][window.id]) reportDataByStudentId[studentId][window.id] = {};
      reportDataByStudentId[studentId][window.id].latestScore = formattedLatestScore;
    }
  }

  const activeWindowIndex = windows.findIndex(window => window.isCurrent);
  const activeWindow = windows[activeWindowIndex];
  const mappedActivities = [];
  const mappedStudentIds = {};
  // take the latest activity for each student
  for (let i = activities[selectedLocale].length - 1; i >= 0; i--) {
    const windowActivities = activities[selectedLocale][i];
    for (let j = 0; j < windowActivities.length; j++) {
      if (!mappedStudentIds[windowActivities[j].studentId]) {
        mappedStudentIds[windowActivities[j].studentId] = true;
        mappedActivities.push(windowActivities[j]);
      }
    }
  }
  const studentActivities = cloneDeep(mappedActivities);
  
  const {scores: armScores} = await generateBenchmarkReport(studentActivities, "ARM", "ARM", selectedBenchmark, customBenchmarks, selectedLocale, students, false, activeWindow);
  const {scores: wcpmScores} = await generateBenchmarkReport(studentActivities, "ORF", "WCPM", selectedBenchmark, customBenchmarks, selectedLocale, students, false, activeWindow);
  
  // include latest ARM PR for thread overview
  for (const armScore of armScores) {
    if (isInvalidScore(armScore)) continue;
    const {ui, PR} = armScore;
    const {value: studentId, label: name} = ui;
    if (!(studentId in reportDataByStudentId)) reportDataByStudentId[studentId] = {name};
    reportDataByStudentId[studentId].armPR = PR;
  }

  // include latest WCPM PR for thread overview
  for (const wcpmScore of wcpmScores) {
    if (isInvalidScore(wcpmScore)) continue;
    const {ui, PR} = wcpmScore;
    const {value: studentId, label: name} = ui;
    if (!(studentId in reportDataByStudentId)) reportDataByStudentId[studentId] = {name};
    reportDataByStudentId[studentId].wcpmPR = PR;
  }

  // add students with no activities into report
  for (const student of students) {
    if (!studentOrder.includes(student.id)) {
      studentOrder.push(student.id);
      reportDataByStudentId[student.id] = {
        studentId: student.id,
        name: student.ui.label
      };
    }
  }

  const masteriesByStudentId = masteries[selectedLocale];

  return studentOrder.map(studentId => {
    const reportData = reportDataByStudentId[studentId];
    const masteryData = masteriesByStudentId[studentId];
    // Estimated Mastery logic
    const trend = {}
    if (metricId === 'ARM' && studentTrends) {
      const matchedStudentTrend = studentTrends[selectedLocale].find(t => t.studentId === studentId);
      if (matchedStudentTrend && matchedStudentTrend.trend) {
        const window = windows.find(w => w.isCurrent).id
        trend["EM"] = {       
          PR: getPR({
            score: matchedStudentTrend.trend,
            scale: scaleId,
            grade: selectedBenchmark.grade,
            language: selectedLocale,
            window,
          }),
          scaleId,
          score: matchedStudentTrend.trend,
        };
      } else {
        trend["EM"] = {};
      }
    }
    return { studentId, ...reportData, ...masteryData, ...trend }
  });
}

function isInvalidScore(score) {
  return score.status === states.ERROR || score.status === "UNDER_REVIEW" || score.displayScore === null;
}